import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { View, Dimensions, ScrollView, Keyboard } from 'react-native';
import { Text, TextInput } from '../../components';
import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';

import { Heading } from '../../components';
import { PrimaryButton } from '../../design/components';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useForm } from '../../../common/utils/hooks';
import { useDialogContext } from '../../../common/utils/context';
import { getFullMachineName } from '../../../common/utils/machine';

const ServiceRunCashBags = ({
	dockets,
	serviceRun,
}) => {
	const styles = useThemedStyles(createStyles);
	const [isFormSubmitted, setIsFormSubmitted] = useState(false);
	const { closeDialog } = useDialogContext();

	const [setServiceRunCashBags] = useMutation(SET_SERVICE_RUN_CASH_BAGS);
	const [setServiceRunCashBag] = useMutation(SET_SERVICE_RUN_CASH_BAG);

	const onSubmit = useCallback(() => {
		setIsFormSubmitted(true);
	}, []);

	const { values, changeFormValue } = useForm(serviceRun.isOneCashBag ? { cashBag: serviceRun.cashBag } : dockets.reduce((acc, docket) => {
		if (docket.cashBag) {
			acc[docket.id] = docket.cashBag;
		}

		return acc;
	}, {}));

	useEffect(() => {
		if (isFormSubmitted) {
			const submitForm = async () =>  {
				if (serviceRun.isOneCashBag) {
					await setServiceRunCashBag({ variables: { input: { id: serviceRun.id, cashBag: values.cashBag } } });
				}
				else {
					await setServiceRunCashBags({ variables: { values } });
				}
				closeDialog();
			};
			Keyboard.dismiss();
			setTimeout(submitForm, 100); // required to wait for keyboard dismiss https://github.com/wix/react-native-navigation/issues/2318
		}
	}, [isFormSubmitted, closeDialog, setServiceRunCashBags, setServiceRunCashBag, values, serviceRun.isOneCashBag, serviceRun.id]);

	const filteredDockets = useMemo(() => dockets.filter(docket => docket.collectCash), [dockets]);

	return (
		<View>
			<Heading heading={serviceRun.isOneCashBag ? 'Service run cash bag' : 'Service run cash bags'} />
			<View>
				<ScrollView
					scrollIndicatorInsets={{ right: 1 }}
					style={{ maxHeight: Dimensions.get('window').height - 300 }}
					contentContainerStyle={styles.list}
				>
					{
						serviceRun.isOneCashBag && (
							<TextInput
								value={values.cashBag || ''}
								onChange={value => changeFormValue({ cashBag: value })}
								selectTextOnFocus={true}
								placeholder="Enter cash bag"
							/>
						)
					}
					{
						!serviceRun.isOneCashBag && filteredDockets.map(docket => (
							<View key={docket.id} style={styles.field}>
								<Text style={styles.machineName}>{ getFullMachineName(docket.machine, docket.machine.place, docket.machine.location) }</Text>
								<TextInput
									value={values[docket.id] || ''}
									onChange={value => changeFormValue({ [docket.id]: value })}
									selectTextOnFocus={true}
									placeholder="Enter cash bag"
								/>
							</View>
						))
					}
				</ScrollView>
			</View>
			<PrimaryButton
				style={styles.submit}
				size="compact"
				label="OK"
				loading={isFormSubmitted}
				onPress={onSubmit}
			/>
		</View>
	);
};

const createStyles = ({ ui: { colors, type, space } }) => ({
	list: {
		gap: space.md,
	},
	field: {
		gap: space.xs,
	},
	machineName: {
		...type.bodySmallStrong,
		color: colors.text,
	},
	submit: {
		marginTop: space.md,
	},
});

const SET_SERVICE_RUN_CASH_BAGS = gql`
	mutation setServiceRunCashBags (
		$values: JSON!,
	) {
		setServiceRunCashBags (
			values: $values,
		) {
			id
			cashBag
		}
	}
`;

const SET_SERVICE_RUN_CASH_BAG = gql`
	mutation modifyServiceRunMutation($input: ModifyServiceRunInput!) {
		modifyServiceRun(input: $input) {
			servicerun {
				id
				cashBag
			}
		}
	}
`;

export default ServiceRunCashBags;
